import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { getReportsGroupedByCategory } from '@data/config/reports';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import CollapseLeftIcon from '@data/assets/collapseLeft.svg';
import CaretDownIcon from '@data/assets/caret-down.svg';
import CaretUpIcon from '@data/assets/caret-up.svg';
import EngagementFeedIcon from '@data/assets/engagementFeed.svg';
import GEIcon from '@data/assets/geIcon.svg';
import LEIcon from '@data/assets/leIcon.svg';
import OverviewReportIcon from '@data/assets/overviewReport.svg';
import type { ReportPath } from '@data/config/reports';
import type { ReactNode } from 'react';

// Icon rended next to each report in nav
function ReportIcon({ path }: { path: ReportPath }): ReactNode {
  switch (path) {
    case 'overview':
      return <OverviewReportIcon aria-hidden className="size-4 shrink-0" />;
    case 'engagement':
      return <EngagementFeedIcon aria-hidden className="size-4 shrink-0" />;
    case 'lexile-measure':
      return <LEIcon aria-hidden className="size-4 shrink-0" />;
    case 'ge-score':
      return <GEIcon aria-hidden className="size-4 shrink-0" />;
    default:
      return null;
  }
}

/**
 * Config-driven report navigation.
 *
 * Reads the current :productId from the URL and renders grouped nav links
 * for all reports available to that product.
 *
 * Active report link is highlighted automatically via NavLink.
 *
 * On mobile: renders as a full-width collapsible panel with a
 * "Show / Hide reports menu" toggle button.
 * On tablet+: renders as the existing side-panel with a vertical collapse option.
 */
export function ReportNav() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const { data: applications } = useApplications();

  // Preserve the selected date range (`from`/`to`) when switching between
  // reports within a product, mirroring how drill-down links carry it. Other
  // params (classId/studentId) are drilldown-specific and intentionally dropped
  // so each report opens at its top level.
  const preservedSearch = useMemo(() => {
    const next = new URLSearchParams();
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      next.set('from', from);
      next.set('to', to);
    }
    return next.toString();
  }, [searchParams]);

  // Extract the user's product codes for the current application
  const userProductCodes = applications
    ?.find((app) => app.applicationCode === productId)
    ?.products.map((p) => p.productCode);

  const grouped = getReportsGroupedByCategory(
    productId ?? '',
    userProductCodes
  );
  const categories = Object.entries(grouped);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Reset mobileOpen when the viewport crosses into tablet+ so that
  // the collapsed state is never overridden by a stale mobileOpen=true.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // On mobile the panel is always full-width; collapsed only applies at tablet+
  const showFullContent = !collapsed || mobileOpen;

  return (
    <nav aria-label="Reports" className="w-full tablet:w-auto">
      {/* ── Mobile toggle (visible only below tablet breakpoint) ── */}
      <div className="mb-1 tablet:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex w-full items-center gap-3 rounded-lg bg-white px-2 py-3"
        >
          {mobileOpen ? (
            <CaretUpIcon aria-hidden className="size-4 shrink-0" />
          ) : (
            <CaretDownIcon aria-hidden className="size-4 shrink-0" />
          )}
          <span className="text-base font-semibold text-neutral-1100">
            {mobileOpen ? 'Hide reports menu' : 'Show reports menu'}
          </span>
        </button>
      </div>

      {/* ── Nav panel ── */}
      {/* On mobile: shown/hidden by mobileOpen; on tablet+: always visible */}
      <div
        className={[
          'overflow-visible rounded-lg bg-white shadow-md',
          // mobile visibility
          mobileOpen ? 'block' : 'hidden',
          // tablet+: always visible, width-transition for collapse
          'tablet:block tablet:transition-[width] tablet:duration-300 tablet:ease-in-out',
          collapsed ? 'tablet:w-12' : 'tablet:w-[300px]',
        ].join(' ')}
      >
        {/* Top bar with collapse affordance — tablet+ only */}
        <div
          className={[
            'hidden border-b border-neutral-200 p-4 tablet:flex',
            collapsed ? 'justify-center' : 'justify-end',
          ].join(' ')}
        >
          <button
            type="button"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            onClick={() => {
              setCollapsed((prev) => !prev);
              setMobileOpen(false);
            }}
            className="h-4 text-neutral-600 hover:text-neutral-900"
          >
            <CollapseLeftIcon
              aria-hidden
              className={[
                'h-[10px] w-[14px]',
                collapsed ? 'rotate-180' : '',
              ].join(' ')}
            />
          </button>
        </div>

        {/* Category groups */}
        {categories.map(([category, reports], idx) => (
          <div key={category}>
            {/* Divider between categories (not before the first) */}
            {idx > 0 && <div className="border-t border-neutral-400" />}

            {/* Category label — hidden when collapsed on tablet+ */}
            {showFullContent && (
              <div className="mb-1 flex h-[26px] items-center px-4 py-1">
                <span className="text-xs font-semibold text-neutral-700">
                  {category}
                </span>
              </div>
            )}

            {/* Report links */}
            <ul>
              {reports?.map((report) => (
                <li key={report.path}>
                  <NavLink
                    to={{ pathname: report.path, search: preservedSearch }}
                    title={collapsed ? report.label : undefined}
                    className={({ isActive }) =>
                      [
                        'flex items-center transition-colors',
                        !showFullContent
                          ? 'justify-center px-0 py-3 tablet:justify-center'
                          : 'gap-2 px-4 py-3',
                        'text-sm font-medium text-neutral-1300',
                        isActive
                          ? 'border-l-[3px] border-brand-flyGuyPurple bg-purple-100'
                          : 'border-l-[3px] border-transparent hover:bg-neutral-100',
                      ].join(' ')
                    }
                  >
                    <ReportIcon path={report.path} />
                    {showFullContent && <span>{report.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}
